// src/services/api.js
import apiWithAuth from './apiWithAuth';

const api = apiWithAuth;

// User CRUD operations
export const fetchUserDetails = (userId) =>
    api.get('/users/onboarding-data', { params: { userId } });

export const updateUserStatus = (userId, status) =>
    api.put(`/users/onboarding-docs/${userId}`, { status });

export const deleteUserDocuments = (userId) =>
    api.put(`/users/onboarding-docs/re-submit/${userId}?type=docs`, {});

export const updateUserProfile = (userId, profileData) =>
    api.put('/admin-panel/onboarding/profile', profileData, { params: { userId } });

// Update user email and/or phone with duplicate check
export const updateUserContact = (userId, contactData) =>
    api.put('/admin-panel/onboarding/update-contact', contactData, { params: { userId } });

// Shop CRUD operations
export const updateShopStatus = (shopId, status) =>
    api.put(`/shops/status/${shopId}`, { status });

export const updateShopDetails = (shopId, shopData) =>
    api.put(`/shops/${shopId}`, shopData);

export const updateShopProfile = (shopId, shopName) =>
    api.put('/profile', { shopName }, { params: { shopId } });

export const updateShopBankDetails = (shopId, bankDetailsBody) =>
    api.put(`/shops/${shopId}`, bankDetailsBody);

export const updateShopAddress = (shopId, addressData) =>
    api.put(`/shops/${shopId}`, addressData);

export const updateShopMedia = (shopId, mediaData) => {
    // If mediaData contains File objects, send as multipart/form-data
    const images = mediaData?.shopImages;
    const videos = mediaData?.shopVideos;
    const containsFiles = [images, videos].some(
        (arr) => Array.isArray(arr) && arr.some((item) => item instanceof File || item instanceof Blob)
    );

    if (containsFiles) {
        const formData = new FormData();
        if (Array.isArray(images)) {
            images.forEach((file) => {
                if (file) formData.append('shopImages', file);
            });
        }
        if (Array.isArray(videos)) {
            videos.forEach((file) => {
                if (file) formData.append('shopVideos', file);
            });
        }
        return api.put(`/shops/${shopId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
    // Otherwise, assume it's JSON with URLs arrays
    return api.put(`/shops/${shopId}`, mediaData);
};

export const updateShopTimings = (shopId, timingData) =>
    api.put(`/shops/${shopId}`, timingData);

export const updateShopContact = (shopId, contactData) =>
    api.put(`/shops/${shopId}`, contactData);

// Coupons API
export const fetchShopCoupons = (shopId, filters = {}) => {
    const { type, showToCustomerOnly, validForOnlinePaymentsOnly, active, expired } = filters || {};
    const params = { shopId };
    if (type) params.type = type;
    if (showToCustomerOnly !== undefined) params.showToCustomerOnly = !!showToCustomerOnly;
    if (validForOnlinePaymentsOnly !== undefined) params.validForOnlinePaymentsOnly = !!validForOnlinePaymentsOnly;
    if (active !== undefined) params.active = !!active;
    if (expired !== undefined) params.expired = !!expired;
    return api.get('/admin-panel/coupons', { params });
};

export const createShopCoupon = (couponBody) =>
    api.post('/admin-panel/coupons', couponBody);

export const updateShopCoupon = (shopId, couponCode, couponBody) =>
    api.put('/admin-panel/coupons', couponBody, { params: { shopId, couponCode } });

export const deleteShopCoupon = (shopId, couponCode) =>
    api.delete('/admin-panel/coupons', { params: { shopId, couponCode } });

// Products API (Admin Panel)
export const fetchShopProducts = (shopId, params = {}) => {
    const query = { shopId, ...params };
    return api.get('/admin-panel/products', { params: query });
};

// Categories API - returns category trees (filtered by active)
export const fetchCategoryTree = (params = {}) => {
    return api.get('/admin-panel/categories', { params });
};

export const fetchProductById = (id) =>
    api.get(`/admin-panel/products/${id}`);

export const createProduct = (body) =>
    api.post('/admin-panel/products', body);

export const bulkAddProducts = (body) =>
    api.post('/admin-panel/products/bulk-add-products', body);

export const updateProduct = (id, body) =>
    api.put(`/admin-panel/products/${id}`, body);

export const patchProduct = (id, body) =>
    api.patch(`/admin-panel/products/${id}`, body);

export const deleteProduct = (id, { hard = false } = {}) =>
    api.delete(`/admin-panel/products/${id}`, { params: { hard } });

export const approveProduct = (id, body) =>
    api.patch(`/admin-panel/products/${id}/approve`, body);

export const rateProduct = (id, body) =>
    api.patch(`/admin-panel/products/${id}/rating`, body);

// File Upload functions
// Multiple files: POST /upload/multiple, field "files" (max 10 per product media). Backend: type from query; shopId/userId from req.body or req.query → { file_urls }
export const uploadMultipleFiles = (files, shopId, userId, type) => {
    const formData = new FormData();
    if (Array.isArray(files)) {
        files.forEach((file) => {
            if (file) formData.append('files', file);
        });
    } else {
        formData.append('files', files);
    }
    if (shopId != null) formData.append('shopId', shopId);
    if (userId != null) formData.append('userId', userId);
    return api.post('/upload/multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { type, shopId, userId },
    });
};

// Video upload: POST /upload/video, field "files" (max 10). Backend: type from query; shopId/userId from req.body or req.query → { file_url }
export const uploadVideo = (files, shopId, userId, type) => {
    const formData = new FormData();
    if (Array.isArray(files)) {
        files.forEach((file) => {
            if (file) formData.append('files', file);
        });
    } else {
        formData.append('files', files);
    }
    if (shopId != null) formData.append('shopId', shopId);
    if (userId != null) formData.append('userId', userId);
    return api.post('/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { type, shopId, userId },
    });
};

export const uploadSingleFile = (file, shopId, userId, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('shopId', shopId);
    formData.append('userId', userId);
    formData.append('type', type);
    
    return api.post('/upload/single', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

// Permissions API (only use when absolutely necessary - permissions should be cached)
export const fetchUserPermissions = () =>
    api.get('/employee-auth/permissions');

// Force refresh permissions (clears cache and fetches fresh)
export const refreshUserPermissions = async () => {
    // Clear cache first
    try {
        localStorage.removeItem('kd_admin_permissions');
        localStorage.removeItem('kd_admin_permissions_timestamp');
    } catch (e) {
        console.warn('Failed to clear permissions cache:', e);
    }
    
    return api.get('/employee-auth/permissions');
};

export default api;
